import { faCheck, faTicket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAuth from '@hooks/useAuth';
import { Progress } from '@ui';
import { HTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { cn } from '~/lib/cn';
import { Module } from '../../../types';
import { useClubSlug } from '../../Clubs/hooks';
import { Voucher } from '../types';
import useVoucherValidation from './useVoucherValidation';

interface Props extends HTMLAttributes<HTMLDivElement> {
    type: Module;
}

/**
 * The vouchers the booker already owns, offered as a choice in the same visual language as
 * the payment methods it sits under — picking one is, after all, how they pay.
 */
const VoucherCards = ({ type, className, ...props }: Props) => {
    const { t } = useTranslation('voucher');
    const clubSlug = useClubSlug();
    const { watch, setValue } = useFormContext();
    const { isAuthenticated } = useAuth();
    const { validate, validateResult, isInvalid, renderVoucherValue } = useVoucherValidation(type);

    const { data } = useSWR<{ data: Voucher[] }>(
        isAuthenticated ? `user/card-vouchers?type=${type}&club=${clubSlug}` : null
    );

    const space_voucher_card_id = watch('space_voucher_card_id');
    const voucher_code = watch('voucher_code');
    const vouchers = data?.data ?? [];

    if (space_voucher_card_id || vouchers.length === 0) return null;

    const handleSelect = (voucher: Voucher) => {
        // pressing the selected one again releases it, back to paying normally
        const isSelected = voucher_code?.toLowerCase() === voucher.code?.toLowerCase();
        setValue('voucher_code', isSelected ? '' : voucher.code);
        if (!isSelected) validate(voucher.code);
    };

    return (
        <div className={className} {...props}>
            <span className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-accents-8">
                {t('fields.savedTitle')}
            </span>

            <div className="tw-flex tw-flex-col tw-gap-2">
                {vouchers.map((voucher) => {
                    const isSelected = voucher_code?.toLowerCase() === voucher.code?.toLowerCase();
                    // a discount voucher has nothing to use up, so it has no meter to show
                    const remaining = voucher.type === 'discount' ? 1 : 1 - voucher.value_used / voucher.value;

                    return (
                        <div
                            key={voucher.id}
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={0}
                            onClick={() => handleSelect(voucher)}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                event.preventDefault();
                                handleSelect(voucher);
                            }}
                            className={cn(
                                'tw-cursor-pointer tw-rounded-xl tw-border-2 tw-border-solid tw-bg-white tw-p-3 tw-transition-colors',
                                isSelected
                                    ? 'tw-border-success'
                                    : 'tw-border-accents-2 hover:tw-border-accents-5 hover:tw-bg-accents-0'
                            )}
                        >
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-successLight">
                                    <FontAwesomeIcon icon={faTicket} className="tw-text-success" />
                                </span>
                                <div className="tw-min-w-0 tw-flex-1">
                                    <span className="tw-block tw-font-semibold tw-text-accents-9">
                                        {t('type.' + voucher.type)}
                                    </span>
                                    <span className="tw-block tw-truncate tw-text-sm tw-tracking-wide tw-text-accents-6">
                                        {voucher.code?.toUpperCase()}
                                    </span>
                                </div>
                                <span className="tw-shrink-0 tw-text-sm tw-font-semibold tw-text-accents-9">
                                    {voucher.type === 'discount'
                                        ? `${voucher.percentage} %`
                                        : voucher.type === 'value'
                                          ? `${(voucher.value - voucher.value_used).toFixed(2)} €`
                                          : t('fields.charges', {
                                                count:
                                                    Math.round((voucher.value - voucher.value_used) * 100) / 100
                                            })}
                                </span>
                                <span
                                    className={cn(
                                        'tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid',
                                        isSelected
                                            ? 'tw-border-success tw-bg-success tw-text-white'
                                            : 'tw-border-accents-5 tw-bg-white'
                                    )}
                                >
                                    {isSelected && <FontAwesomeIcon icon={faCheck} className="tw-text-[10px]" />}
                                </span>
                            </div>
                            {voucher.type !== 'discount' && (
                                <Progress
                                    className="tw-mt-3"
                                    size="xs"
                                    color={voucher.is_active ? 'success' : 'error'}
                                    progress={remaining}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {(isInvalid || !!validateResult) && (
                <span
                    className={cn(
                        isInvalid ? 'tw-text-error' : 'tw-text-success',
                        'tw-mt-2 tw-block tw-pl-2 tw-text-xs tw-italic'
                    )}
                >
                    {isInvalid ? t('fields.invalidCode') : t('fields.recognizedApplied', { value: renderVoucherValue })}
                </span>
            )}
        </div>
    );
};

export default VoucherCards;
